import { useEffect, useState, useCallback, useRef } from "react";
import { api, fileUrl, authedFileUrl } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Plus, ImageIcon, Trash2, Upload, ChevronLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminPhotos() {
    const [albums, setAlbums] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [openAlbum, setOpenAlbum] = useState(false);
    const [albumName, setAlbumName] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const fileRef = useRef(null);
    const [caption, setCaption] = useState("");
    const [uploading, setUploading] = useState(false);

    const loadAlbums = useCallback(async () => {
        setLoading(true);
        const r = await api.get("/albums");
        setAlbums(r.data.albums);
        setLoading(false);
    }, []);

    useEffect(() => { loadAlbums(); }, [loadAlbums]);

    const loadAlbum = async (album) => {
        setSelected(album);
        const r = await api.get(`/albums/${album.id}/photos`);
        setPhotos(r.data.photos);
    };

    const createAlbum = async (e) => {
        e.preventDefault();
        if (!albumName.trim()) return;
        setSubmitting(true);
        try {
            await api.post("/admin/albums", { album_name: albumName });
            toast.success("Album created");
            setAlbumName("");
            setOpenAlbum(false);
            loadAlbums();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setSubmitting(false);
        }
    };

    const deleteAlbum = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/admin/albums/${confirmDelete.id}`);
            toast.success("Album deleted");
            setConfirmDelete(null);
            setSelected(null);
            loadAlbums();
        } catch (err) { toast.error("Failed"); }
    };

    const uploadPhoto = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setUploading(true);
        let ok = 0, fail = 0;
        for (const f of files) {
            try {
                const fd = new FormData();
                fd.append("file", f);
                fd.append("caption", caption);
                await api.post(`/admin/albums/${selected.id}/photos`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                ok++;
            } catch (err) { fail++; }
        }
        if (ok) toast.success(`${ok} photo(s) uploaded`);
        if (fail) toast.error(`${fail} failed (only JPG / under 500MB)`);
        setUploading(false);
        setCaption("");
        if (fileRef.current) fileRef.current.value = "";
        loadAlbum(selected);
    };

    const deletePhoto = async (id) => {
        try {
            await api.delete(`/admin/photos/${id}`);
            toast.success("Deleted");
            setPhotos((ps) => ps.filter((p) => p.id !== id));
        } catch { toast.error("Failed"); }
    };

    if (!selected) {
        return (
            <div className="space-y-6" data-testid="admin-photos-page">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">Gallery</div>
                        <h1 className="font-heading text-3xl font-black tracking-tight mt-1">Photo Albums</h1>
                        <p className="text-muted-foreground text-sm mt-1">Create albums and upload JPG photos for parents.</p>
                    </div>
                    <Button onClick={() => setOpenAlbum(true)} className="rounded-full" data-testid="new-album-btn">
                        <Plus className="h-4 w-4 mr-2" /> New Album
                    </Button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
                ) : albums.length === 0 ? (
                    <div className="text-center py-16 border border-dashed border-border rounded-3xl">
                        <ImageIcon className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">No albums yet. Create your first one!</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {albums.map((a) => (
                            <Card key={a.id} className="rounded-2xl overflow-hidden cursor-pointer hover:-translate-y-1 hover:shadow-md transition-all" onClick={() => loadAlbum(a)} data-testid={`admin-album-${a.id}`}>
                                <div className="aspect-[4/3] bg-muted overflow-hidden">
                                    {a.thumbnail ? (
                                        <img src={authedFileUrl(a.thumbnail)} alt={a.album_name} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                            <ImageIcon className="h-10 w-10" />
                                        </div>
                                    )}
                                </div>
                                <CardContent className="p-4">
                                    <div className="font-bold font-heading">{a.album_name}</div>
                                    <div className="text-xs text-muted-foreground mt-1">{a.photo_count} photos</div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                <Dialog open={openAlbum} onOpenChange={setOpenAlbum}>
                    <DialogContent className="rounded-2xl">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Create Album</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={createAlbum} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Album name</Label>
                                <Input value={albumName} onChange={(e) => setAlbumName(e.target.value)} required className="h-11 rounded-xl" placeholder="Sports Day 2026" data-testid="album-name-input" />
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setOpenAlbum(false)} className="rounded-full">Cancel</Button>
                                <Button type="submit" disabled={submitting} className="rounded-full" data-testid="album-create-btn">
                                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-album-detail-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <Button variant="ghost" onClick={() => setSelected(null)} className="rounded-full -ml-3" data-testid="admin-back-albums-btn">
                        <ChevronLeft className="h-4 w-4 mr-1" /> All Albums
                    </Button>
                    <h1 className="font-heading text-3xl font-black tracking-tight mt-2">{selected.album_name}</h1>
                    <p className="text-muted-foreground text-sm">{photos.length} photos</p>
                </div>
                <Button variant="destructive" onClick={() => setConfirmDelete(selected)} className="rounded-full" data-testid="delete-album-btn">
                    <Trash2 className="h-4 w-4 mr-2" /> Delete Album
                </Button>
            </div>

            <Card className="rounded-2xl">
                <CardContent className="p-5 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-2 space-y-2">
                            <Label>Optional caption (applied to all uploaded photos)</Label>
                            <Input value={caption} onChange={(e) => setCaption(e.target.value)} className="h-11 rounded-xl" data-testid="photo-caption-input" />
                        </div>
                        <div className="space-y-2">
                            <Label>JPG files</Label>
                            <input
                                ref={fileRef}
                                type="file"
                                accept=".jpg,.jpeg,image/jpeg"
                                multiple
                                onChange={uploadPhoto}
                                className="hidden"
                                data-testid="photo-file-input"
                            />
                            <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full h-11 rounded-full" data-testid="photo-upload-btn">
                                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Upload className="h-4 w-4 mr-2" /> Upload JPG(s)</>)}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {photos.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-3xl">
                    <ImageIcon className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No photos yet. Upload some JPGs above.</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {photos.map((p) => (
                        <div key={p.id} className="group relative aspect-square rounded-2xl overflow-hidden bg-muted">
                            <img src={authedFileUrl(p.image_url)} alt={p.caption} className="w-full h-full object-cover" />
                            <button
                                onClick={() => deletePhoto(p.id)}
                                className="absolute top-2 right-2 bg-destructive text-destructive-foreground rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`delete-photo-${p.id}`}
                                aria-label="Delete photo"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                            {p.caption && (
                                <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/70 to-transparent text-white text-xs">{p.caption}</div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this album?</AlertDialogTitle>
                        <AlertDialogDescription>All photos in &quot;{confirmDelete?.album_name}&quot; will be removed.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={deleteAlbum} className="rounded-full bg-destructive text-destructive-foreground" data-testid="confirm-delete-album">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
